'use client';

import { c as _c } from "react/compiler-runtime";
import { m, Variants } from 'motion/react';
import { SPRING, STAGGER } from '@/lib/motion-utils';
import { ReactNode } from 'react';
import React from 'react';
import { cn } from '@/lib/utils';

// https://21st.dev/ibelick/animated-group/default

type PresetType = 'fade' | 'slide' | 'scale' | 'blur' | 'blur-slide' | 'zoom' | 'flip' | 'bounce' | 'rotate' | 'swing';
type AnimatedGroupProps = {
  children: ReactNode;
  className?: string;
  variants?: {
    container?: Variants;
    item?: Variants;
  };
  preset?: PresetType;
};
const defaultContainerVariants: Variants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: STAGGER.tight
    }
  }
};
const defaultItemVariants: Variants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1
  }
};

// `slide`/`blur-slide` are generic entrances and share the SPRING.reveal token
// (also used by RecentlyPhotos, video-player). The playful presets below
// (zoom/flip/bounce/rotate/swing) intentionally keep bespoke single-use springs
// — each one's feel is part of its identity, and a one-use token would be noise.
const presetVariants: Record<PresetType, {
  container: Variants;
  item: Variants;
}> = {
  fade: {
    container: defaultContainerVariants,
    item: {
      hidden: {
        opacity: 0
      },
      visible: {
        opacity: 1
      }
    }
  },
  slide: {
    container: defaultContainerVariants,
    item: {
      hidden: {
        opacity: 0,
        y: 20
      },
      visible: {
        opacity: 1,
        y: 0,
        transition: SPRING.reveal
      }
    }
  },
  scale: {
    container: defaultContainerVariants,
    item: {
      hidden: {
        opacity: 0,
        scale: 0.9
      },
      visible: {
        opacity: 1,
        scale: 1
      }
    }
  },
  blur: {
    container: defaultContainerVariants,
    item: {
      hidden: {
        opacity: 0,
        filter: 'blur(4px)'
      },
      visible: {
        opacity: 1,
        filter: 'blur(0px)'
      }
    }
  },
  'blur-slide': {
    container: defaultContainerVariants,
    item: {
      hidden: {
        opacity: 0,
        filter: 'blur(4px)',
        y: 20
      },
      visible: {
        opacity: 1,
        filter: 'blur(0px)',
        y: 0,
        transition: SPRING.reveal
      }
    }
  },
  zoom: {
    container: defaultContainerVariants,
    item: {
      hidden: {
        opacity: 0,
        scale: 0.9
      },
      visible: {
        opacity: 1,
        scale: 1,
        transition: {
          type: 'spring',
          stiffness: 300,
          damping: 20
        }
      }
    }
  },
  flip: {
    container: defaultContainerVariants,
    item: {
      hidden: {
        opacity: 0,
        rotateX: -90
      },
      visible: {
        opacity: 1,
        rotateX: 0,
        transition: {
          type: 'spring',
          stiffness: 300,
          damping: 20
        }
      }
    }
  },
  bounce: {
    container: defaultContainerVariants,
    item: {
      hidden: {
        opacity: 0,
        y: -50
      },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          type: 'spring',
          stiffness: 400,
          damping: 10
        }
      }
    }
  },
  rotate: {
    container: defaultContainerVariants,
    item: {
      hidden: {
        opacity: 0,
        rotate: -180
      },
      visible: {
        opacity: 1,
        rotate: 0,
        transition: {
          type: 'spring',
          stiffness: 200,
          damping: 15
        }
      }
    }
  },
  swing: {
    container: defaultContainerVariants,
    item: {
      hidden: {
        opacity: 0,
        rotate: -10
      },
      visible: {
        opacity: 1,
        rotate: 0,
        transition: {
          type: 'spring',
          stiffness: 300,
          damping: 8
        }
      }
    }
  }
};
function AnimatedGroup(t0) {
  const $ = _c(13);
  const {
    children,
    className,
    variants,
    preset
  } = t0;
  let t1;
  if ($[0] !== preset) {
    t1 = preset ? presetVariants[preset] : {
      container: defaultContainerVariants,
      item: defaultItemVariants
    };
    $[0] = preset;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedVariants = t1;
  const containerVariants = variants?.container || selectedVariants.container;
  const itemVariants = variants?.item || selectedVariants.item;
  let t2;
  if ($[2] !== className) {
    t2 = cn(className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== itemVariants) {
    let t4;
    if ($[7] !== itemVariants) {
      t4 = child => <m.div key={React.isValidElement(child) && child.key != null ? child.key : JSON.stringify(child)} variants={itemVariants}>{child}</m.div>;
      $[7] = itemVariants;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    t3 = React.Children.map(children, t4);
    $[4] = children;
    $[5] = itemVariants;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[9] !== containerVariants || $[10] !== t2 || $[11] !== t3) {
    t4 = <m.div initial="hidden" animate="visible" variants={containerVariants} className={t2}>{t3}</m.div>;
    $[9] = containerVariants;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
export { AnimatedGroup };